import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { actions, type AuthResponse, events, getFieldErrorMessage, views } from '@cx-web/feature-auth';
import { CX_LOGIN_SUCCESS } from '@cx-web/feature-auth/events';
import { REPUB_CONFIG } from '@cx-web/repub-config';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { Button } from '@fpsm/cxy-components/Button';
import { Checkbox } from '@fpsm/cxy-components/Checkbox';
import { Input } from '@fpsm/cxy-components/Input';

import { type Repub } from '@cx-modules/constants';

import { type View, VIEW_FORGOT_PASSWORD, VIEW_SIGNUP } from '~/apps/product-plus/providers/auth-modal-provider';
import { trackLoginFailure, trackLoginSuccess } from '~/tracking/tracking';

import styles from './styles/LoginForm.module.css';

export type LoginFormProps = {
  close: () => void;
  openAtView: (view: View) => void;
  repub: Repub;
  repubPath: string;
};

export const LoginForm = ({ close, openAtView, repub, repubPath }: LoginFormProps) => {
  const fetcher = useFetcher<AuthResponse>();
  const loginSuccessRef = useRef(false);
  const [showErrorState, setShowErrorState] = useState(false);
  const { t } = useTranslation(['label']);

  const data = fetcher.data;
  const hasErrorMessage = data?.message && !data?.success;

  // handle local error
  useEffect(() => {
    if (fetcher.state === 'idle' && (data?.fieldErrors || hasErrorMessage)) {
      setShowErrorState(true);
    }
  }, [data?.fieldErrors, hasErrorMessage, data?.message, fetcher.state]);

  // handle api success
  useEffect(() => {
    if (data?.success && !loginSuccessRef.current) {
      loginSuccessRef.current = true;

      trackLoginSuccess('auth_modal', { repub, userId: data.userId! });

      window.dispatchEvent(new CustomEvent(CX_LOGIN_SUCCESS, { detail: data }));

      close();
    }
  }, [close, data, openAtView, repub]);

  // handle api error
  const email = fetcher.formData?.get('email');
  useEffect(() => {
    if (hasErrorMessage && email && data) {
      trackLoginFailure('auth_modal', {
        email: email.toString(),
        error: data.message ?? '',
        repub,
      });
    }
  }, [hasErrorMessage, email, data, repub]);

  const onChange = () => {
    setShowErrorState(false);
  };

  return (
    <fetcher.Form
      action={`${repubPath}/plus/auth/login`}
      className={styles.loginForm}
      data-testid="login-form"
      method="post"
      onSubmit={() => {
        trackEvent(events.LOG_IN_ATTEMPT, {
          eventAction: actions.LOGIN,
          method: REPUB_CONFIG[repub]?.tracking.authProvider,
          view: views.LOGIN,
        });
      }}
    >
      <Input
        aria-describedby="email-error"
        autoComplete="email"
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.email)].filter(Boolean) as string[]) : undefined
        }
        id="email"
        key="email"
        name="email"
        onChange={onChange}
        placeholder="Email"
        type="email"
      />
      <Input
        aria-describedby="password-error"
        autoComplete="current-password"
        errors={
          showErrorState ? ([getFieldErrorMessage(data?.fieldErrors?.password)].filter(Boolean) as string[]) : undefined
        }
        id="password"
        key="password"
        name="password"
        onChange={onChange}
        placeholder="Password"
        type="password"
      />
      {hasErrorMessage && showErrorState && (
        <div className={styles.errorMessage} id="generic-error">
          {data.message}
        </div>
      )}
      <div className={styles.buttonSection}>
        <Button isLoading={fetcher.state !== 'idle'} size="large" type="submit" variant="secondary">
          {t('CX_AUTH_LOGIN')}
        </Button>
      </div>
      <div className={styles.checkboxSection}>
        <Checkbox defaultChecked id="remember" name="remember" shouldHighlightOnChecked={false} />
        <label className={styles.checkboxLabel} htmlFor="keep-logged-in">
          {t('CX_AUTH_KEEP_ME_LOGGED_IN')}
        </label>
        <a
          className={styles.forgotPassword}
          href={`${repubPath}/plus/auth/forgot-password`}
          onClick={e => {
            e.preventDefault();
            openAtView(VIEW_FORGOT_PASSWORD);
          }}
        >
          {t('CX_AUTH_FORGOT_PASSWORD_QUESTION')}
        </a>
      </div>
      <p className={styles.signupText}>
        {t('CX_AUTH_DONT_HAVE_ACCOUNT')}{' '}
        <a
          className={styles.signupLink}
          href={`${repubPath}/plus/auth/signup`}
          onClick={e => {
            e.preventDefault();
            openAtView(VIEW_SIGNUP);
          }}
        >
          {t('CX_AUTH_SIGNUP')}
        </a>
      </p>
    </fetcher.Form>
  );
};

LoginForm.displayName = 'LoginForm';
